import {
  UserManager,
  WebStorageStateStore,
  User as OidcUser,
  Log,
} from "oidc-client-ts";
import { User } from "../types";

// Configure logging
Log.setLogger(console);
Log.setLevel(Log.DEBUG);

// Create and export UserManager instance to ensure singleton usage across the app
const createUserManager = () => {
  // Get window storage access
  const storageWindow = window;

  // OIDC Configuration with carefully considered settings
  const oidcConfig = {
    authority:
      process.env.REACT_APP_AUTH_AUTHORITY ||
      "https://api.appdevus.platform.navify.com/api/v1/auth/protocols/oidc/",
    client_id:
      process.env.REACT_APP_CLIENT_ID || "b454e9b9-69e5-4aba-a5b8-89733598989c",
    redirect_uri: `${window.location.origin}/callback`,
    response_type: "code",
    scope: "default openid profile email",
    post_logout_redirect_uri: `${window.location.origin}`,

    // State and PKCE management (critical for avoiding "No matching state" errors)
    // Use sessionStorage for state
    stateStore: new WebStorageStateStore({
      store: storageWindow.sessionStorage,
    }),
    userStore: new WebStorageStateStore({ store: storageWindow.localStorage }),

    // Explicitly enable PKCE
    usePkce: true,

    // Features configuration - completely disable silent renewal
    automaticSilentRenew: false,
    filterProtocolClaims: true,
    loadUserInfo: false, // Skip loading user info as requested

    // Performance and reliability settings
    clockSkew: 300, // 5 minutes tolerance for clock skew
    monitorSession: false, // Don't monitor session for changes
  };

  // Create and return the UserManager instance
  return new UserManager(oidcConfig);
};

// Create singleton UserManager instance
const userManager = createUserManager();

export const AuthService = {
  // Get the internal UserManager instance (for testing/debugging)
  getUserManager: () => userManager,

  // Initialize the AuthService with event handlers
  init: () => {
    // UserManagerEvents doesn't have clearAllEvents, so we handle events carefully
    // to avoid duplicate event handlers

    // Listen for essential events
    userManager.events.addUserLoaded((user) => {
      console.log(
        "OIDC user loaded event",
        user
          ? `User ID: ${user.profile.sub}, expires: ${
              user.expires_at
                ? new Date(user.expires_at * 1000).toISOString()
                : "unknown"
            }`
          : "No user"
      );
    });

    userManager.events.addSilentRenewError((error) => {
      console.error("OIDC silent renew error", error);
    });

    userManager.events.addUserSignedOut(() => {
      console.log("OIDC user signed out event");
    });

    userManager.events.addAccessTokenExpiring(() => {
      console.log("OIDC access token expiring event");
    });

    userManager.events.addAccessTokenExpired(() => {
      console.log("OIDC access token expired event");
      // Could automatically redirect to login here if needed
    });

    // Listen for state/storage events to help diagnose issues
    window.addEventListener("storage", (event) => {
      console.log(
        `Storage event: key=${
          event.key
        }, oldValue exists=${!!event.oldValue}, newValue exists=${!!event.newValue}`
      );
    });

    console.log("Auth service initialized");
  },

  // Login user and use PKCE flow
  loginWithRedirect: async () => {
    try {
      console.log("Preparing for login redirect");

      // Clear any stale states and data before initiating login
      await userManager.clearStaleState();

      // Clear any previous OIDC state that might be causing conflicts
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith("oidc.")) {
          console.log(`Clearing session storage item: ${key}`);
          sessionStorage.removeItem(key);
        }
      });

      // Store the current URL to return to after login
      sessionStorage.setItem("redirectUrl", window.location.pathname);

      // Start the login flow with PKCE
      console.log("Starting signin redirect flow");

      // Create a custom state value to help with debugging
      const timestamp = new Date().getTime();
      const stateId = `auth-${timestamp}`;

      // Start signin with a specific state
      await userManager.signinRedirect({ state: stateId });
      console.log("Signin redirect initiated with state:", stateId);
    } catch (error) {
      console.error("Login redirect failed:", error);
      throw error;
    }
  },

  // Handle the authentication callback
  handleLoginCallback: async () => {
    try {
      console.log("Processing authentication callback");

      // Use the current URL for callback processing
      const url = window.location.href;
      console.log("Callback URL being processed:", url);

      // Parse URL parameters to check for state manually
      const urlParams = new URLSearchParams(window.location.search);
      const stateParam = urlParams.get("state");
      const codeParam = urlParams.get("code");

      console.log("URL parameters for callback:", {
        state: stateParam,
        code: codeParam ? "present" : "missing",
      });

      if (!stateParam || !codeParam) {
        console.error("Missing required parameters in callback URL");
        throw new Error("Invalid callback URL - missing parameters");
      }

      // Examine localStorage for debugging
      const lsStateKey = `oidc.state.${stateParam}`;
      console.log(
        `Looking for state in localStorage: ${lsStateKey}`,
        !!localStorage.getItem(lsStateKey)
      );

      // 1. First try the signinRedirectCallback (most reliable for code flow)
      try {
        console.log("Attempting signinRedirectCallback...");
        const user = await userManager.signinRedirectCallback(url);
        console.log("Successfully authenticated with signinRedirectCallback");
        return user ? transformUser(user) : null;
      } catch (innerError) {
        console.warn(
          "signinRedirectCallback failed, trying alternative approach",
          innerError
        );

        // 2. Try with signinCallback as fallback
        console.log("Attempting signinCallback as fallback...");
        const user = await userManager.signinCallback();
        console.log("Successfully authenticated with signinCallback fallback");
        return user ? transformUser(user) : null;
      }
    } catch (error) {
      console.error("Login callback failed:", error);

      // Enhanced diagnostics for debugging
      console.group("Authentication callback error details");
      console.log("Error:", error);
      console.log(
        "Error type:",
        error instanceof Error ? error.constructor.name : typeof error
      );
      console.log(
        "Error message:",
        error instanceof Error ? error.message : String(error)
      );

      // Check URL parameters in detail
      const urlParams = new URLSearchParams(window.location.search);
      console.log("URL parameters:", {
        state: urlParams.get("state"),
        code: urlParams.has("code") ? "present" : "missing",
        error: urlParams.get("error"),
        error_description: urlParams.get("error_description"),
      });

      // Check storage state in both localStorage and sessionStorage
      console.log("--- LocalStorage keys ---");
      const lsKeys = Object.keys(localStorage);
      const lsOidcKeys = lsKeys.filter((key) => key.startsWith("oidc."));
      console.log("OIDC localStorage keys:", lsOidcKeys);
      lsOidcKeys.forEach((key) => {
        console.log(
          `${key}: ${
            localStorage.getItem(key) ? "[content exists]" : "[empty]"
          }`
        );
      });

      console.log("--- SessionStorage keys ---");
      const ssKeys = Object.keys(sessionStorage);
      const ssOidcKeys = ssKeys.filter((key) => key.startsWith("oidc."));
      console.log("OIDC sessionStorage keys:", ssOidcKeys);
      ssOidcKeys.forEach((key) => {
        console.log(
          `${key}: ${
            sessionStorage.getItem(key) ? "[content exists]" : "[empty]"
          }`
        );
      });

      console.groupEnd();

      throw error;
    }
  },

  // Logout
  logout: async () => {
    try {
      console.log("Initiating logout");
      await userManager.signoutRedirect();
    } catch (error) {
      console.error("Logout failed:", error);
      throw error;
    }
  },

  // Get the current user from storage
  getCurrentUser: async () => {
    try {
      const user = await userManager.getUser();
      console.log(
        "Got current user:",
        user ? `ID: ${user.profile.sub}` : "null"
      );
      return user ? transformUser(user) : null;
    } catch (error) {
      console.error("Get current user failed:", error);
      return null;
    }
  },

  // Get the access token
  getAccessToken: async () => {
    try {
      const user = await userManager.getUser();
      return user?.access_token || null;
    } catch (error) {
      console.error("Get access token failed:", error);
      return null;
    }
  },

  // Attempt to refresh the token explicitly
  refreshToken: async () => {
    try {
      console.log("Attempting to refresh token");
      // Use signinSilent without any redirects
      const user = await userManager.signinSilent({
        silentRequestTimeoutInSeconds: 10, // Reasonable timeout
      });
      console.log("Token refreshed successfully");
      return user ? transformUser(user) : null;
    } catch (error) {
      console.error("Token refresh failed:", error);
      throw error;
    }
  },

  // Check if user is authenticated and refresh token if needed
  isAuthenticated: async () => {
    try {
      const user = await userManager.getUser();

      // If no user, user is definitely not authenticated
      if (!user) {
        console.log("No user found in storage");
        return false;
      }

      // If token is not expired, user is authenticated
      if (!user.expired) {
        console.log("User is authenticated with valid token");
        return true;
      }

      // Token is expired, try to refresh
      console.log("Token expired, attempting refresh");
      try {
        // Use signinSilent with a reasonable timeout
        const newUser = await userManager.signinSilent({
          silentRequestTimeoutInSeconds: 10,
        });
        const isValid = !!newUser && !newUser.expired;
        console.log("Token refresh result:", isValid ? "success" : "failed");
        return isValid;
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        return false;
      }
    } catch (error) {
      console.error("Authentication check failed:", error);
      return false;
    }
  },
};

// Transform OIDC User to our User model
function transformUser(oidcUser: OidcUser): User {
  return {
    id: oidcUser.profile.sub as string,
    email: (oidcUser.profile.email as string) || "",
    firstName: oidcUser.profile.given_name as string | undefined,
    lastName: oidcUser.profile.family_name as string | undefined,
    acceptedTerms: !!(oidcUser.profile.accepted_terms as boolean),
    acceptedPrivacy: !!(oidcUser.profile.accepted_privacy as boolean),
    termsVersion: oidcUser.profile.terms_version as string | undefined,
    privacyVersion: oidcUser.profile.privacy_version as string | undefined,
  };
}

// Initialize auth service
AuthService.init();

export default AuthService;
